import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Box,
  Paper,
} from "@mui/material";
import {
  SelectAll as SelectAllIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import { useOutlookPlugin } from "../../../hooks/useOutlookPlugin";
import type { Reminder } from "@services/demo/msoffice/types";
import { ActionCard } from "./ActionCard";

interface RemindersListComponentProps {
  onReminderClick?: (reminder: Reminder) => void;
  onUrgentCountChange?: (count: number) => void;
  fetchReminders: () => Promise<{ data: Reminder[] }>;
  hideDraftButton?: boolean;
}

export const RemindersListComponent: React.FC<RemindersListComponentProps> = ({
  onUrgentCountChange,
  fetchReminders,
  hideDraftButton,
}) => {
  const { actions } = useOutlookPlugin();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkedReminders, setCheckedReminders] = useState<Set<string>>(new Set());

  const fetchRemindersRef = useRef(fetchReminders);
  fetchRemindersRef.current = fetchReminders;

  useEffect(() => {
    const loadReminders = async () => {
      try {
        setLoading(true);
        const response = await fetchRemindersRef.current();
        setReminders(response.data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load reminders");
      } finally {
        setLoading(false);
      }
    };

    loadReminders();
  }, []);

  // Notify parent of total count
  useEffect(() => {
    if (onUrgentCountChange && !loading) {
      onUrgentCountChange(reminders.length);
    }
  }, [reminders.length, loading, onUrgentCountChange]);

  const handleExecute = (reminder: Reminder) => {
    if (reminder.actionType === "draft_email" && reminder.draftConfig) {
      actions.draftEmail(
        reminder.draftConfig.to,
        reminder.draftConfig.subject,
        reminder.draftConfig.body,
        reminder.draftConfig.attachments
      );
    }
  };

  const handleCheckboxChange = (reminderId: string) => {
    setCheckedReminders((prev) => {
      const next = new Set(prev);
      if (next.has(reminderId)) {
        next.delete(reminderId);
      } else {
        next.add(reminderId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (checkedReminders.size === reminders.length) {
      setCheckedReminders(new Set());
    } else {
      setCheckedReminders(new Set(reminders.map((r) => r.id)));
    }
  };

  const handleExecuteAll = () => {
    const selected = reminders.filter((r) => checkedReminders.has(r.id));
    for (const reminder of selected) {
      handleExecute(reminder);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 100,
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" style={{ marginLeft: 8 }}>
            Loading reminders...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" variant="outlined">
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const allSelected = checkedReminders.size === reminders.length && reminders.length > 0;

  return (
    <>
      <Card sx={{ borderRadius: 0, pb: checkedReminders.size > 0 ? 7 : 0 }}>
        <CardContent>
          {reminders.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No actions available
            </Typography>
          ) : (
            <Box>
              {reminders.map((reminder) => (
                <ActionCard
                  key={reminder.id}
                  id={reminder.id}
                  title={reminder.name}
                  type={reminder.actionType || "draft_email"}
                  buttonLabel="Draft"
                  draftConfig={reminder.draftConfig}
                  checked={checkedReminders.has(reminder.id)}
                  onCheckedChange={handleCheckboxChange}
                  onExecute={() => handleExecute(reminder)}
                  hideButton={hideDraftButton}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Floating bottom bar when reminders are selected */}
      {checkedReminders.size > 0 && (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            zIndex: 999,
            bgcolor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            size="small"
            startIcon={<SelectAllIcon />}
            onClick={handleSelectAll}
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
          >
            {allSelected ? "Deselect All" : "Select All"}
          </Button>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {checkedReminders.size} selected
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<PlayArrowIcon />}
            onClick={handleExecuteAll}
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              bgcolor: "#1565C0",
              "&:hover": { bgcolor: "#0D47A1" },
            }}
          >
            Execute All
          </Button>
        </Paper>
      )}
    </>
  );
};
